import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { renderMarkdown } from "../utils/markdown";
import iconImage from "../assets/icon.png";
import { useBranding } from "@/hooks/useBranding";
import { User } from "lucide-react";
import { parseSuggestions } from "@/utils/parseSuggestions";
import SuggestionButtons from "@/components/SuggestionButtons";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  showAvatar?: boolean;
  pipelineIds?: Set<string>;
  onTogglePipeline?: (schoolId: string, schoolName: string) => void;
  suggestions?: string[];
  onSuggestionClick?: (text: string) => void;
  isLastMessage?: boolean;
}

const ChatMessage = ({
  role,
  content,
  showAvatar = true,
  pipelineIds,
  onTogglePipeline,
  suggestions = [],
  onSuggestionClick,
  isLastMessage = false,
}: ChatMessageProps) => {
  const { branding } = useBranding();
  const assistantIcon = branding?.chatIdleIconUrl ?? iconImage;
  const navigate = useNavigate();
  const proseRef = useRef<HTMLDivElement>(null);

  // Mark stars as "starred" for schools already in the pipeline
  useEffect(() => {
    if (!proseRef.current || !pipelineIds) return;
    const stars = proseRef.current.querySelectorAll<HTMLButtonElement>(".school-star");
    stars.forEach(btn => {
      const id = btn.dataset.schoolId;
      if (id && pipelineIds.has(id)) {
        btn.classList.add("starred");
      } else {
        btn.classList.remove("starred");
      }
    });
  }, [pipelineIds, content]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Handle star button clicks
      const starBtn = (e.target as HTMLElement).closest(".school-star") as HTMLButtonElement | null;
      if (starBtn) {
        e.preventDefault();
        e.stopPropagation();
        const schoolId = starBtn.dataset.schoolId;
        const schoolName = starBtn.dataset.schoolName;
        if (schoolId && schoolName && onTogglePipeline) {
          // Optimistically toggle the starred class
          starBtn.classList.toggle("starred");
          onTogglePipeline(schoolId, schoolName);
        }
        return;
      }

      // Handle internal link clicks (SPA navigation)
      const anchor = (e.target as HTMLElement).closest(
        "a[data-internal-link]"
      ) as HTMLAnchorElement | null;
      if (anchor) {
        e.preventDefault();
        navigate(anchor.getAttribute("href")!);
      }
    },
    [navigate, onTogglePipeline]
  );

  if (role === "assistant") {
    const { cleanContent, suggestions: parsedSuggestions } = parseSuggestions(content);
    const activeSuggestions = isLastMessage
      ? suggestions.length > 0
        ? suggestions
        : parsedSuggestions
      : [];

    return (
      <div className="flex gap-3 px-4 md:px-6 py-2">
        <div className="flex-shrink-0 w-8 pt-1">
          {showAvatar ? (
            <img
              src={assistantIcon}
              alt="golfrecruit.ai"
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="bg-card rounded-2xl border border-border/60 shadow-sm px-5 py-4 md:px-6 md:py-5">
            <div
              ref={proseRef}
              className="chat-prose w-full min-w-0 overflow-hidden"
              onClick={handleClick}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(cleanContent) }}
            />
          </div>
          {activeSuggestions.length > 0 && onSuggestionClick && (
            <SuggestionButtons suggestions={activeSuggestions} onSelect={onSuggestionClick} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-end gap-3 px-4 md:px-6 py-2">
      <div className="max-w-[85%]">
        <p className="bg-primary/10 dark:bg-primary/20 rounded-2xl px-5 py-3 text-foreground leading-relaxed whitespace-pre-wrap break-words text-left">
          {content}
        </p>
      </div>
      <div className="flex-shrink-0 w-8 pt-1">
        {showAvatar ? (
          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-muted">
            <User className="h-5 w-5 text-muted-foreground" />
          </div>
        ) : (
          <div className="w-8" />
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
